import React, {useState, useEffect, useRef, useCallback} from 'react';
import {toast} from 'react-toastify';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import aiAssistantService from '@app/services/aiAssistant';
import {addExportButtonsToTables} from '@app/utils/csvExport';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './AIDBAssistant.css';

interface Message {
  role: 'user' | 'assistant' | 'system' | 'thinking';
  content: string;
  timestamp: number;
  toolName?: string;
  isThinking?: boolean;
}

const AIDBAssistant: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentResponse, setCurrentResponse] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const permissions = useSelector((state: any) => state.auth.permissions);
  const navigate = useNavigate();
  const [t] = useTranslation();
  
  // Refs for streaming updates without re-renders
  const streamingContentRef = useRef<string>('');
  const updateTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isStreamingRef = useRef<boolean>(false);
  const autoScrollRef = useRef<boolean>(true);
  const lastMessageCountRef = useRef<number>(0);

  useEffect(() => {
    if (!permissions?.['ai-db-assistant']?.menu) {
      toast.error(t('auth.noAccess'));
      navigate('/');
    }
  }, []);

  // Smooth scroll to bottom without behavior to prevent jumps
  const scrollToBottom = useCallback((smooth = false) => {
    if (messagesEndRef.current && autoScrollRef.current) {
      messagesEndRef.current.scrollIntoView({
        behavior: smooth ? 'smooth' : 'auto',
        block: 'end'
      });
    }
  }, []);

  // Auto-scroll during streaming
  useEffect(() => {
    if (isStreamingRef.current) {
      scrollToBottom(false); // Instant scroll during streaming
    }
  }, [currentResponse, scrollToBottom]);

  // Smooth scroll when new messages arrive (not during streaming)
  useEffect(() => {
    if (!isStreamingRef.current && messages.length > lastMessageCountRef.current) {
      lastMessageCountRef.current = messages.length;
      setTimeout(() => scrollToBottom(true), 100);
    }
  }, [messages, scrollToBottom]);

  // Add CSV export buttons only when streaming is complete
  useEffect(() => {
    if (!isLoading && !isStreamingRef.current && messagesContainerRef.current) {
      const timer = setTimeout(() => {
        addExportButtonsToTables(messagesContainerRef.current!, 'ai-db-assistant');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isLoading, messages.length]); // Only depend on message count, not full messages array

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (updateTimerRef.current) {
        clearTimeout(updateTimerRef.current);
      }
    };
  }, []);

  // Optimized chunk handler - batch updates to reduce flicker
  const handleResponseChunk = useCallback((chunk: string) => {
    // Accumulate chunks in ref
    streamingContentRef.current = streamingContentRef.current === '' 
      ? chunk 
      : streamingContentRef.current + chunk;
    
    // Update current response for display
    setCurrentResponse(streamingContentRef.current);
    
    // Throttle message array updates to reduce re-renders (every 100ms instead of 50ms)
    if (!updateTimerRef.current) {
      updateTimerRef.current = setTimeout(() => {
        const content = streamingContentRef.current;
        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'assistant') {
            // Create new array with updated last message
            return [
              ...prev.slice(0, -1),
              {
                ...lastMsg,
                content: content
              }
            ];
          }
          return prev;
        });
        updateTimerRef.current = null;
      }, 100); // Increased from 50ms to 100ms for smoother updates
    }
  }, []);

  useEffect(() => {
    // Connect to AI Assistant
    aiAssistantService.connect({
      onConnect: () => {
        setIsConnected(true);
      },
      onDisconnect: () => {
        setIsConnected(false);
      },
      onStatus: (status) => {
        // Don't show status messages
      },
      onThinkingText: (event) => {
        // Add thinking message showing LLM's reasoning
        setMessages((prev) => {
          // Remove any existing typing indicator
          const filtered = prev.filter(msg => msg.content !== '...');
          
          // Check if last message is already a thinking message
          const lastMsg = filtered[filtered.length - 1];
          if (lastMsg && lastMsg.role === 'thinking') {
            // Update existing thinking message
            return [
              ...filtered.slice(0, -1),
              {
                ...lastMsg,
                content: event.text,
                timestamp: Date.now()
              }
            ];
          }
          
          // Add new thinking message
          const thinkingMessage: Message = {
            role: 'thinking',
            content: event.text,
            timestamp: Date.now(),
            isThinking: true
          };
          return [...filtered, thinkingMessage];
        });
      },
      onToolStart: (event) => {
        // Tool execution happens after thinking - don't show separate message
      },
      onToolComplete: (event) => {
        // Tool completed - don't show separate message
      },
      onToolError: (event) => {
        // Show tool errors
        const errorMessage: Message = {
          role: 'system',
          content: event.message,
          timestamp: Date.now()
        };
        setMessages((prev) => [...prev, errorMessage]);
      },
      onResponseStart: () => {
        // Mark streaming as active
        isStreamingRef.current = true;
        
        // Remove thinking messages and add typing indicator
        setMessages((prev) => {
          const filtered = prev.filter(msg => msg.role !== 'thinking');
          const typingMessage: Message = {
            role: 'assistant',
            content: '',
            timestamp: Date.now()
          };
          return [...filtered, typingMessage];
        });
        setCurrentResponse('');
        streamingContentRef.current = '';
      },
      onResponseChunk: handleResponseChunk,
      onResponseComplete: (response) => {
        // Mark streaming as complete
        isStreamingRef.current = false;
        
        // Clear any pending timer
        if (updateTimerRef.current) {
          clearTimeout(updateTimerRef.current);
          updateTimerRef.current = null;
        }
        
        // Update the last message with final response
        setMessages((prev) => {
          const lastMsg = prev[prev.length - 1];
          if (lastMsg && lastMsg.role === 'assistant') {
            return [
              ...prev.slice(0, -1),
              {
                ...lastMsg,
                content: response,
                timestamp: Date.now()
              }
            ];
          }
          return prev;
        });
        
        setCurrentResponse('');
        setIsLoading(false);
        setStatusMessage('');
        streamingContentRef.current = '';
        
        // Smooth scroll to bottom after completion
        setTimeout(() => scrollToBottom(true), 100);
      },
      onError: (error) => {
        // Mark streaming as complete
        isStreamingRef.current = false;
        
        // Clear any pending timer
        if (updateTimerRef.current) {
          clearTimeout(updateTimerRef.current);
          updateTimerRef.current = null;
        }
        
        // Remove typing indicator and add error message
        setMessages((prev) => {
          const updated = prev.filter(msg => msg.content !== '...' && msg.content !== '');
          return [
            ...updated,
            {
              role: 'system',
              content: `Error: ${error.message}`,
              timestamp: Date.now()
            }
          ];
        });
        setCurrentResponse('');
        setIsLoading(false);
        setStatusMessage('');
        streamingContentRef.current = '';
        toast.error(error.message);
      }
    });

    return () => {
      aiAssistantService.disconnect();
    };
  }, [handleResponseChunk]);

  const handleSendMessage = () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: Date.now()
    };

    // Add user message
    setMessages((prev) => [...prev, userMessage]);
    
    // Don't add typing indicator here - wait for onResponseStart
    setIsLoading(true);
    aiAssistantService.sendMessage(inputMessage);
    setInputMessage('');
    
    // Scroll to show user message
    setTimeout(() => scrollToBottom(true), 50);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderMessageContent = (content: string) => {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({children}) => (
            <p style={{marginBottom: '0.75rem', lineHeight: '1.7', color: '#212529'}}>
              {children}
            </p>
          ),
          h1: ({children}) => (
            <h3 style={{marginTop: '1.25rem', marginBottom: '0.75rem', fontWeight: 'bold', fontSize: '1.4rem', color: '#212529'}}>
              {children}
            </h3>
          ),
          h2: ({children}) => (
            <h4 style={{marginTop: '1rem', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '1.2rem', color: '#212529'}}>
              {children}
            </h4>
          ),
          h3: ({children}) => (
            <h5 style={{marginTop: '0.75rem', marginBottom: '0.5rem', fontWeight: 'bold', fontSize: '1.1rem', color: '#212529'}}>
              {children}
            </h5>
          ),
          ul: ({children}) => (
            <ul style={{marginBottom: '0.75rem', marginLeft: '1.5rem', lineHeight: '1.6'}}>
              {children}
            </ul>
          ),
          ol: ({children}) => (
            <ol style={{marginBottom: '0.75rem', marginLeft: '1.5rem', lineHeight: '1.6'}}>
              {children}
            </ol>
          ),
          li: ({children}) => (
            <li style={{marginBottom: '0.25rem'}}>
              {children}
            </li>
          ),
          a: ({href, children}) => (
            <a 
              href={href} 
              target="_blank" 
              rel="noopener noreferrer"
              style={{color: '#007bff', textDecoration: 'underline'}}
            >
              {children}
            </a>
          ),
          strong: ({children}) => (
            <strong style={{fontWeight: '600', color: '#212529'}}>
              {children}
            </strong>
          ),
          em: ({children}) => (
            <em style={{fontStyle: 'italic'}}>
              {children}
            </em>
          ),
          code: ({inline, children}: any) => {
            if (inline) {
              return (
                <code style={{
                  backgroundColor: '#f8f9fa',
                  padding: '2px 6px',
                  borderRadius: '3px',
                  fontSize: '0.9em',
                  color: '#d63384',
                  fontFamily: 'monospace'
                }}>
                  {children}
                </code>
              );
            }
            return (
              <pre style={{
                backgroundColor: '#f8f9fa',
                padding: '1rem',
                borderRadius: '4px',
                overflow: 'auto',
                marginBottom: '0.75rem'
              }}>
                <code style={{fontFamily: 'monospace', fontSize: '0.9em'}}>
                  {children}
                </code>
              </pre>
            );
          },
          blockquote: ({children}) => (
            <blockquote style={{
              borderLeft: '4px solid #dee2e6',
              paddingLeft: '1rem',
              marginLeft: '0',
              marginBottom: '0.75rem',
              color: '#6c757d',
              fontStyle: 'italic'
            }}>
              {children}
            </blockquote>
          ),
          table: ({children}) => (
            <div style={{overflowX: 'auto', marginBottom: '0.75rem'}}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                border: '1px solid #dee2e6'
              }}>
                {children}
              </table>
            </div>
          ),
          thead: ({children}) => (
            <thead style={{backgroundColor: '#f8f9fa'}}>
              {children}
            </thead>
          ),
          th: ({children}) => (
            <th style={{
              padding: '0.5rem',
              border: '1px solid #dee2e6',
              textAlign: 'left',
              fontWeight: '600'
            }}>
              {children}
            </th>
          ),
          td: ({children}) => (
            <td style={{
              padding: '0.5rem',
              border: '1px solid #dee2e6'
            }}>
              {children}
            </td>
          ),
          hr: () => (
            <hr style={{
              border: 'none',
              borderTop: '1px solid #dee2e6',
              margin: '1rem 0'
            }} />
          )
        }}
      >
        {content}
      </ReactMarkdown>
    );
  };

  return (
    <div className="ai-db-assistant-page">
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>
                <i className="fas fa-robot mr-2" />
                AI DB Assistant
              </h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <a href="/">Home</a>
                </li>
                <li className="breadcrumb-item active">AI DB Assistant</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid" style={{height: 'calc(100vh - 180px)'}}>
          <div className="row" style={{height: '100%'}}>
            <div className="col-12" style={{display: 'flex', flexDirection: 'column'}}>
              <div className="card" style={{flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0}}>
                <div className="card-header">
                  <h3 className="card-title">
                    <span
                      className={`badge badge-${isConnected ? 'success' : 'danger'} mr-2`}
                    >
                      {isConnected ? 'Connected' : 'Disconnected'}
                    </span>
                    Chat with AI about your database
                  </h3>
                </div>

                <div className="card-body p-0" style={{flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 0}}>
                  <div className="chat-container">
                    <div 
                      className="chat-messages" 
                      ref={messagesContainerRef}
                      onScroll={(e) => {
                        // Detect if user scrolled up manually
                        const element = e.currentTarget;
                        const isAtBottom = element.scrollHeight - element.scrollTop <= element.clientHeight + 50;
                        autoScrollRef.current = isAtBottom;
                      }}
                    >
                      {messages.length === 0 && !currentResponse && (
                        <div className="empty-state">
                          <i className="fas fa-comments" />
                          <h4>Start a conversation with the AI DB Assistant</h4>
                          <p>
                            Ask questions about your database, queries, or data
                            analysis
                          </p>
                        </div>
                      )}

                      {messages.map((msg, index) => (
                        <div key={index} className="chat-message">
                          <div className={`message-avatar ${msg.role === 'thinking' ? 'assistant' : msg.role}`}>
                            <i
                              className={`fas ${
                                msg.role === 'user'
                                  ? 'fa-user'
                                  : msg.role === 'system'
                                  ? 'fa-exclamation-triangle'
                                  : msg.role === 'thinking'
                                  ? 'fa-brain'
                                  : 'fa-robot'
                              }`}
                            />
                          </div>
                          <div className={`message-content ${msg.role === 'thinking' ? 'thinking' : msg.role}`}>
                            {msg.content === '' || msg.content === '...' ? (
                              <div className="typing-indicator">
                                <span className="typing-dot" />
                                <span className="typing-dot" />
                                <span className="typing-dot" />
                              </div>
                            ) : (
                              <>
                                <div className="message-text">
                                  {msg.role === 'thinking' ? msg.content : renderMessageContent(msg.content)}
                                </div>
                                <div className="message-time">
                                  {formatTimestamp(msg.timestamp)}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      ))}

                      <div ref={messagesEndRef} />
                    </div>

                    <div className="chat-input-area">
                      <div className="chat-input-wrapper">
                        <textarea
                          placeholder="Ask about your database..."
                          value={inputMessage}
                          onChange={(e) => setInputMessage(e.target.value)}
                          onKeyPress={handleKeyPress}
                          disabled={!isConnected || isLoading}
                          rows={2}
                        />
                        <button
                          type="button"
                          className="btn btn-primary"
                          onClick={handleSendMessage}
                          disabled={
                            !isConnected || isLoading || !inputMessage.trim()
                          }
                        >
                          {isLoading ? (
                            <i className="fas fa-circle-notch fa-spin" />
                          ) : (
                            <>
                              <i className="fas fa-paper-plane mr-1" />
                              Send
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AIDBAssistant;
